import React, { useState, useEffect, useRef } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import { initialNodeStyle, initialDirectedEdgeStyle } from "./GraphStyle";
import "./CytoscapeGraph.css";
import TopologicalSort from "./TopologicalSort";
import SCC from "./SCC";

const DAG = ({ algorithmType }) => {
  const cyStyle = {
    width: "100%",
    height: "100%",
  };

  const resetState = () => {
    setNodes([]);
    setEdges([]);
    setVisited([]);
    setQueue([]);
    setAdjacencyList({});
    setGenerateGraph(false);
  };

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [visited, setVisited] = useState([]);
  const [queue, setQueue] = useState([]);
  const [cytoscapeReady, setCytoscapeReady] = useState(false); // Track Cytoscape readiness

  const [numNodes, setNumNodes] = useState(2); // Number of nodes
  const [adjacencyList, setAdjacencyList] = useState({}); // Adjacency list
  const [generateGraph, setGenerateGraph] = useState(false);

  // Create a ref to store the Cytoscape instance
  const cytoscapeRef = useRef(null);

  useEffect(() => {
    if (generateGraph) {
      // Generate graph elements
      const generatedElements = generateGraphElements(numNodes, adjacencyList);
      setNodes(generatedElements.nodes);
      setEdges(generatedElements.edges);
      if (cytoscapeRef.current) {
        cytoscapeRef.current.fit();
      }
    }
  }, [generateGraph, numNodes, adjacencyList]);

  const generateGraphElements = (numNodes, adjacencyList) => {
    const generatedNodes = [];
    const generatedEdges = [];

    // Calculate the number of rows and columns for the grid
    const numRows = Math.ceil(Math.sqrt(numNodes));
    const numCols = Math.ceil(numNodes / numRows);

    // Calculate the spacing between nodes
    const spacingX = 300 / numCols;
    const spacingY = 300 / numRows;

    // Generate nodes in a grid
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const nodeId = row * numCols + col + 1;
        if (nodeId <= numNodes) {
          const positionY = row * spacingY + (col % 2 === 0 ? 0 : spacingY / 2);
          generatedNodes.push({
            data: { id: `node${nodeId}`, label: `${nodeId}` },
            position: { x: col * spacingX, y: positionY },
          });
        }
      }
    }

    // Generate edges based on the user's adjacency list
    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      targetNodes.forEach((targetNode) => {
        generatedEdges.push({
          data: {
            id: `edge${sourceNode}-${targetNode}`,
            source: `node${sourceNode}`,
            target: `node${targetNode}`,
          },
        });
      });
    }

    return { nodes: generatedNodes, edges: generatedEdges };
  };

  const handleNumNodesChange = (e) => {
    const newNumNodes = parseInt(e.target.value);

    // Ensure that the number of nodes is within the desired range
    if (newNumNodes >= 2 && newNumNodes <= 10) {
      setNumNodes(newNumNodes);
    } else if (newNumNodes < 2) {
      setNumNodes(2); // Minimum value of 2
    } else if (newNumNodes > 10) {
      setNumNodes(10); // Maximum value of 10
    }
  };

  const handleAdjacencyChange = (node, adjacentNode) => {
    const updatedAdjacencyList = { ...adjacencyList };

    if (!updatedAdjacencyList[node]) {
      updatedAdjacencyList[node] = [];
    }
    if (!updatedAdjacencyList[adjacentNode]) {
      updatedAdjacencyList[adjacentNode] = [];
    }
    const nodeIndex = updatedAdjacencyList[node].indexOf(adjacentNode);

    if (nodeIndex !== -1) {
      updatedAdjacencyList[node].splice(nodeIndex, 1);
    } else {
      updatedAdjacencyList[node].push(adjacentNode);
    }

    setAdjacencyList(updatedAdjacencyList);
  };

  const handleGenerateGraph = () => {
    setNodes([]);
    setEdges([]);
    setGenerateGraph((prevGenerateGraph) => {
      if (!prevGenerateGraph) {
        setNodes([]);
        setEdges([]);

        const generatedElements = generateGraphElements(
          numNodes,
          adjacencyList
        );
        setNodes(generatedElements.nodes);
        setEdges(generatedElements.edges);
      }
      return !prevGenerateGraph;
    });
  };

  const handleCytoscapeReady = (cy) => {
    if (cy) {
      cy.style()
        .fromJson([
          {
            selector: "node",
            style: {
              ...initialNodeStyle,
            },
          },
          {
            selector: "edge",
            style: {
              ...initialDirectedEdgeStyle,
            },
          },
        ])
        .update();

      setCytoscapeReady(true);
    }
  };

  return (
    <div className="graph-container">
      <div className="left-column">
        <div className="graph-options">
          {algorithmType === "topologicalSort" && (
            <label className="graph-option">
              <b>
                Please ensure that you've created a directed acyclic graph! The
                animation will work for cyclic graphs but it is not the correct
                topological sort order.
              </b>
            </label>
          )}
          {algorithmType === "scc" && (
            <label className="graph-option">
              <b>Go ahead and create a directed graph.</b>
            </label>
          )}
        </div>
        <div className="node-input">
          <label className="node-label">Number of Nodes:</label>
          <input
            className="node-input-field"
            type="number"
            value={numNodes}
            onChange={handleNumNodesChange}
            min="2"
            max="10"
          />
        </div>
        <div className="adjacency-list">
          <label className="adjacency-label">Adjacency List:</label>
          <ul className="adjacency-list-ul">
            {Array.from({ length: numNodes }, (_, i) => i + 1).map((node) => (
              <li key={node}>
                Node {node}:&nbsp;&nbsp;&nbsp;
                {Array.from({ length: numNodes }, (_, i) => i + 1)
                  .filter((adjNode) => adjNode !== node)
                  .map((adjNode) => (
                    <label key={adjNode} className="adjacency-list-label">
                      <input
                        type="checkbox"
                        checked={(adjacencyList[node] || []).includes(adjNode)}
                        onChange={() => handleAdjacencyChange(node, adjNode)}
                      />
                      {adjNode}
                    </label>
                  ))}
              </li>
            ))}
          </ul>
        </div>
        <div className="graph-controls">
          <button
            className="generate-reset-button"
            onClick={handleGenerateGraph}
          >
            Generate Graph
          </button>
          &nbsp;&nbsp;
          <button className="generate-reset-button" onClick={resetState}>
            Reset Graph
          </button>
        </div>
      </div>
      <div className="right-column">
        <div className="cytoscape-container ">
          <CytoscapeComponent
            elements={nodes.concat(edges)}
            style={cyStyle}
            cy={(cy) => {
              cy.userZoomingEnabled(false);
              cytoscapeRef.current = cy;
              handleCytoscapeReady(cy);
            }}
          />
        </div>
        {algorithmType === "topologicalSort" && (
          <TopologicalSort
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
          />
        )}
        {algorithmType === "scc" && (
          <SCC
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
          />
        )}
      </div>
    </div>
  );
};

export default DAG;
